import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { resolvePatientByAthenaId } from 'services/athena-embed';
import { isAllowedAthenaOrigin, parsePatientContextEvent } from 'utils/athena-embed';

type RouterHistory = ReturnType<typeof useHistory>;

// Where SSO sends a launch whose athena patient has no local match.
const ATHENA_PATIENT_ERROR_ROUTE = '/athena-error/patient';

const navigateToAthenaPatient = async (history: RouterHistory, athenaId: string): Promise<void> => {
  try {
    const { data } = await resolvePatientByAthenaId(athenaId);
    history.push(data?.id ? `/patient/?patientId=${data.id}` : ATHENA_PATIENT_ERROR_ROUTE);
  } catch {
    history.push(ATHENA_PATIENT_ERROR_ROUTE);
  }
};

// Handles a single postMessage: navigates to the selected patient only for
// trusted athena origins carrying a `patientContextChanged` payload.
const handlePatientContextMessage = (history: RouterHistory, event: MessageEvent): void => {
  if (!isAllowedAthenaOrigin(event.origin)) {
    return;
  }
  const updatedPatient = parsePatientContextEvent(event.data)?.updatedPatient;
  if (updatedPatient) {
    navigateToAthenaPatient(history, updatedPatient);
  }
};

// Listens for athenaOne's `patientContextChanged` embedded-app message and
// navigates the SPA to the newly selected patient. No-op outside the athenaOne
// iframe, since only trusted athena origins pass the origin check.
export const useAthenaPatientContext = (): void => {
  const history = useHistory();

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => handlePatientContextMessage(history, event);
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [history]);
};
